import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { Col, Row } from 'react-bootstrap';
import { State } from '@progress/kendo-data-query';
import { useContext, useEffect, useMemo, useState } from 'react';

import Popup from '../../popup-window';
import { GameContext } from '../../../contexts/game';
import { TeamStateFull } from '../../../types/state';
import { TabButtonComponent } from '../../tabs/tab-button';
import { GameDocumentContext } from '../../../contexts/game-document';
import { GetTitle } from '../../../utils/game-document/assets/titles';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import { GroupResponse } from '../../../types/responses/group-response';
import { GetItemTitle } from '../../../utils/game-document/assets/items';
import { getDateDuration, getFormattedDateString } from '../../../utils/date';

const initialDataState: State = {
  sort: [{ field: 'name' }],
  ...DefaultGridSettings.initialDataState
};

export interface TeamStateDashboardProps {
  state?: TeamStateFull;
  group?: GroupResponse;
  selectedTeamCode: string;
}

export const TeamStateDashboard = ({ ...props }: TeamStateDashboardProps) => {
  const [gameDocument] = useContext(GameDocumentContext);
  const [game] = useContext(GameContext);

  const [showTitles, setShowTitles] = useState(false);
  const [showInventories, setShowInventories] = useState(false);
  const [dataState, setDataState] = useState<State>(initialDataState);

  const tabs = ['Titles', 'Inventories'];

  const selectedTeam = useMemo(() => {
    return game.gameState?.teams.find((t) => t.code === props.selectedTeamCode);
  }, [game.gameState?.teams, props.selectedTeamCode]);

  const inventories = useMemo(() => {
    return (
      selectedTeam?.inventory?.map((item) => {
        const data = {
          name: GetItemTitle(gameDocument.gameDocument!, item.id, '') ?? '',
          quantity: item.quantity
        };
        return data;
      }) ?? []
    );
  }, [selectedTeam?.inventory]);

  const titles = useMemo(() => {
    return (
      selectedTeam?.titles?.map((item) => {
        const data = {
          name: GetTitle(gameDocument.gameDocument!, item, '') ?? '',
          assignedTo: props.state?.name ?? ''
        };
        return data;
      }) ?? []
    );
  }, [selectedTeam?.titles]);

  return (
    <>
      <Row>
        <Col className={'col-3'}>
          <div className={'d-flex'}>
            <div className={'d-flex justify-content-center w-full'}>
              <div
                className={
                  'd-flex flex-column justify-content-center align-items-center'
                }>
                {props?.state?.avatarImage && (
                  <img
                    width={150}
                    height={150}
                    src={props?.state?.avatarImage}
                    className={'rounded-circle'}
                    alt={props?.state?.name}
                  />
                )}
              </div>
            </div>
          </div>
        </Col>
        <Col>
          <Row>
            <Col>
              <ul className="list-unstyled">
                <li>
                  <strong className={'text-primary mt-2'}>
                    {props?.state?.name}
                  </strong>
                </li>
                <li>
                  <span>Group:</span>
                  <strong> {props?.group?.name ?? ''}</strong>
                </li>
              </ul>
            </Col>
            <Col>
              <ul className="list-unstyled">
                <li>
                  <span>Joined date:</span>{' '}
                  <strong>
                    {props?.state?.createdDateUtc
                      ? getFormattedDateString(
                          new Date(props?.state?.createdDateUtc)
                        )
                      : props?.state?.createdDateUtc
                        ? getFormattedDateString(
                            new Date(props?.state?.createdDateUtc)
                          )
                        : ''}
                  </strong>
                </li>
                <li>
                  <span>Duration:</span>{' '}
                  <strong>
                    {props?.state?.createdDateUtc &&
                      getDateDuration(
                        new Date(props?.state?.createdDateUtc),
                        !game?.gameState?.finishedUtc
                          ? new Date()
                          : new Date(game?.gameState?.finishedUtc)
                      )}
                  </strong>
                </li>
              </ul>
            </Col>
          </Row>
        </Col>
      </Row>

      {game.activityState?.hasGame && game.gameState?.status !== 'Finished' && (
        <div className={'d-flex justify-content-start gap-4 mt-4 w-100'}>
          <div
            className={
              'box-rectangular d-flex flex-column justify-content-center align-items-center'
            }>
            <div className={'d-flex flex-column align-items-center'}>
              <div
                className={
                  'd-flex w-4 h-4 rounded-circle border border-3 border-success bg-white justify-content-center align-items-center'
                }>
                <span className={'text-success fw-bold'}>
                  {selectedTeam?.score ?? 0}
                </span>
              </div>
              <div
                className={
                  'd-flex justify-content-center align-items-center mt-3'
                }>
                <span className={'text-dark fw-bold'}>Score</span>
              </div>
            </div>
          </div>
          <div
            className={
              'box-rectangular cursor-pointer d-flex flex-column justify-content-center align-items-center'
            }
            onClick={() => setShowInventories(true)}>
            <div className={'d-flex flex-column align-items-center'}>
              <div
                className={
                  'd-flex w-4 h-4 rounded-circle border border-3 border-success bg-white justify-content-center align-items-center'
                }>
                <span className={'text-success fw-bold'}>
                  {inventories.length ?? 0}
                </span>
              </div>
              <div
                className={
                  'd-flex justify-content-center align-items-center mt-3'
                }>
                <span className={'text-dark fw-bold'}>Inventories</span>
              </div>
            </div>
          </div>
          <div
            className={
              'box-rectangular cursor-pointer d-flex flex-column justify-content-center align-items-center p-4 fw-bold bg-card-blue'
            }
            onClick={() => setShowTitles(true)}>
            <div className={'d-flex flex-column align-items-center'}>
              <div
                className={
                  'd-flex w-4 h-4 rounded-circle border border-3 border-success bg-white justify-content-center align-items-center'
                }>
                <span className={'text-success fw-bold'}>
                  {titles.length ?? 0}
                </span>
              </div>
              <div
                className={
                  'd-flex justify-content-center align-items-center mt-3'
                }>
                <span className={'text-dark fw-bold'}>Titles</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showTitles || showInventories) && (
        <Popup
          handleClose={() => {
            setShowTitles(false);
            setShowInventories(false);
          }}
          className={'box d-flex flex-column w-80 h-80'}>
          <div className={'d-flex flex-column w-full h-100'}>
            <Row>
              <h1 className={'text-primary mt-2'}>
                <b>{props?.state?.name}</b>
              </h1>
            </Row>
            <Row className={'container-tab-component mb-2'}>
              {tabs.map((item, index) => {
                return (
                  <TabButtonComponent
                    thisTabIndex={index}
                    activeTab={showTitles ? 0 : 1}
                    title={item}
                    onSetActiveTab={(tabIndex) => {
                      if (tabIndex > 0) {
                        setShowInventories(true);
                        setShowTitles(false);
                      } else {
                        setShowInventories(false);
                        setShowTitles(true);
                      }
                    }}
                  />
                );
              })}
              <hr className="mt-0" />
            </Row>
            <Row>
              {showTitles && (
                <Grid
                  id={'grid-titles'}
                  pageable={DefaultGridSettings.pagerSettings}
                  sortable={true}
                  data={titles}
                  {...dataState}
                  onDataStateChange={(e: GridDataStateChangeEvent) => {
                    setDataState(e.dataState);
                  }}>
                  <Column field={'name'} title={'Title'} />
                  <Column field={'assignedTo'} title={'Assigned To'} />

                  <GridNoRecords>
                    <div
                      className={
                        'p-4 d-flex flex-column justify-content-center text-muted'
                      }>
                      <span className="material-symbols-outlined">
                        zone_person_alert
                      </span>
                      <small>No data found</small>
                    </div>
                  </GridNoRecords>
                </Grid>
              )}
              {showInventories && (
                <Grid
                  id={'grid-inventories'}
                  pageable={DefaultGridSettings.pagerSettings}
                  sortable={true}
                  data={inventories}
                  {...dataState}
                  onDataStateChange={(e: GridDataStateChangeEvent) => {
                    setDataState(e.dataState);
                  }}>
                  <Column field={'name'} title={'Item'} />
                  <Column field={'quantity'} title={'Qty'} />

                  <GridNoRecords>
                    <div
                      className={
                        'p-4 d-flex justify-content-center flex-column text-muted'
                      }>
                      <span className="material-symbols-outlined">
                        zone_person_alert
                      </span>
                      <small>No data found</small>
                    </div>
                  </GridNoRecords>
                </Grid>
              )}
            </Row>
          </div>
        </Popup>
      )}
    </>
  );
};
